import React from "react";
import { Mic, X as XIcon, Compass } from "lucide-react";

/**
 * VoiceCommandsGuide
 *
 * Voce del Centro Guida dedicata all'Assistente Vocale "Ora TIME".
 *
 * NOTA: la checkbox "Non mostrare più automaticamente" è stata rimossa.
 * L'unico popup dell'onboarding con quell'opzione è ora quello nella Home.
 * La guida è accessibile in qualsiasi momento dal Centro Guida.
 *
 * Design coerente con le altre guide del Centro Guida:
 *  - backdrop nero traslucido con blur;
 *  - carta glassmorphica bordata di emerald;
 *  - tipografia minimale "Apple-like", parole "Tempo" e "TIME" sempre
 *    formattate correttamente.
 */

// Comandi vocali suddivisi per sezione, coerenti con `lib/voice/commands.js`.
// Concetto: prima si sceglie una MODALITÀ, poi si utilizza una FUNZIONE.
const MODALITA = [
  { spoken: "Ora TIME, modalità TIME Watch", meaning: "Apre la dashboard principale" },
  { spoken: "Ora TIME, modalità Utility", meaning: "Apre la sezione Utility" },
  { spoken: "Ora TIME, modalità Orologio", meaning: "Apre la modalità Orologio" },
];

const FUNZIONI = [
  { spoken: "Ora TIME, opportunità", meaning: "Tempo per lasciare il segno" },
  { spoken: "Ora TIME, oggi", meaning: "Tempo a disposizione oggi" },
  { spoken: "Ora TIME, denaro", meaning: "Patrimonio e denaro" },
  { spoken: "Ora TIME, libertà", meaning: "Tempo libero" },
  { spoken: "Ora TIME, MITEL", meaning: "Portafoglio MITEL" },
  { spoken: "Ora TIME, offerta", meaning: "Tempo dato" },
  { spoken: "Ora TIME, ricompensa", meaning: "Tempo riconosciuto" },
  { spoken: "Ora TIME, timer", meaning: "Apre il Timer" },
  { spoken: "Ora TIME, promemoria", meaning: "Apre i Promemoria" },
  { spoken: "Ora TIME, cronometro", meaning: "Apre il Cronometro" },
  { spoken: "Ora TIME, sveglia", meaning: "Apre la Sveglia" },
  { spoken: "Ora TIME, impronta", meaning: "Apre l'Impronta" },
  { spoken: "Ora TIME, istante", meaning: "Apre Istante" },
  { spoken: "Ora TIME, cosa puoi fare", meaning: "Riapre l'aiuto contestuale" },
];

export default function VoiceCommandsGuide({
  open,
  onClose,
  onBack,
}) {
  if (!open) return null;

  const handleClose = () => {
    onClose && onClose();
  };

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
    } else {
      onClose && onClose();
    }
  };

  return (
    <div
      data-testid="voice-commands-guide"
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="glass-card relative w-full max-w-lg p-6 sm:p-8 border-emerald-400/25 shadow-[0_25px_80px_-20px_rgba(16,185,129,0.35)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          data-testid="voice-commands-guide-close"
          aria-label="Chiudi"
          className="absolute top-4 right-4 p-1 rounded-full text-white/50 hover:text-white hover:bg-white/5 transition"
        >
          <XIcon className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center text-emerald-300">
            <Mic className="w-4 h-4" />
          </div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300">
            Assistente Vocale
          </div>
        </div>
        <h2
          data-testid="voice-commands-guide-title"
          className="text-2xl sm:text-3xl font-light text-white tracking-tight leading-tight mb-3"
        >
          Comandi Vocali
        </h2>
        <p className="text-sm font-light text-white/60 leading-relaxed mb-6">
          Pronuncia sempre la parola d'attivazione{" "}
          <span className="text-emerald-300">&ldquo;Ora TIME&rdquo;</span>{" "}
          seguita dal comando. Scegli una{" "}
          <span className="text-white/85">modalità</span> o direttamente una{" "}
          <span className="text-white/85">funzione</span>.
        </p>

        {/* Sezione 1 — MODALITÀ */}
        <div
          data-testid="voice-commands-guide-section-modalita"
          className="mb-5"
        >
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-2">
            Modalità
          </div>
          <ul className="space-y-2.5">
            {MODALITA.map((ex, i) => (
              <li
                key={`mod-${i}`}
                className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/10"
              >
                <div className="text-emerald-300 mt-0.5 shrink-0">
                  <Mic className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-light text-white leading-snug">
                    <span className="text-emerald-300/90">
                      &ldquo;{ex.spoken}&rdquo;
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-white/45 leading-relaxed">
                    {ex.meaning}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Sezione 2 — FUNZIONI */}
        <div
          data-testid="voice-commands-guide-section-funzioni"
          className="mb-6"
        >
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-2">
            Funzioni
          </div>
          <ul
            data-testid="voice-commands-guide-examples"
            className="space-y-2.5"
          >
            {FUNZIONI.map((ex, i) => (
              <li
                key={`fun-${i}`}
                className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/10"
              >
                <div className="text-emerald-300 mt-0.5 shrink-0">
                  <Mic className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-light text-white leading-snug">
                    <span className="text-emerald-300/90">
                      &ldquo;{ex.spoken}&rdquo;
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-white/45 leading-relaxed">
                    {ex.meaning}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/8">
          <button
            type="button"
            onClick={handleBack}
            data-testid="voice-commands-guide-back"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-[11px] tracking-[0.22em] uppercase text-white/60 hover:text-white hover:border-white/30 transition"
          >
            <Compass className="w-3.5 h-3.5" />
            Torna al Centro Guida
          </button>
          <button
            type="button"
            onClick={handleClose}
            data-testid="voice-commands-guide-ok"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition"
          >
            INIZIA A PARLARE
          </button>
        </div>
      </div>
    </div>
  );
}
